import type {PriceTypeV1} from '@smart/price-preferences-provider';
import {Text} from '@smart/web-components';
import * as React from 'react';
import type {
  PriceDetailType,
  PriceDetailsByType,
} from '../../bff/price-details';
import {useLeasingInformation} from '../hooks/use-leasing-information';
import styles from './leasing-information-table.module.scss';

export interface LeasingInformationTableProps {
  paymentMode: 'leasing' | 'finance';
  priceDetails?: PriceDetailType;
  attributes: PriceDetailsByType['attributes'];
  readonly priceType: PriceTypeV1;
}

export function LeasingInformationTable({
  paymentMode,
  priceDetails,
  attributes,
  priceType,
}: LeasingInformationTableProps): JSX.Element {
  const getPaymentType = (mode?: string) => {
    if (mode === `finance`) return `pcp`;
    if (mode === `leasing` && priceType === `private`) {
      return `pl`;
    }
    return `fsl`;
  };

  const leasingInformation = useLeasingInformation(getPaymentType(paymentMode));

  const data = leasingInformation?.attributes?.find(
    (a) => a.type === getPaymentType(paymentMode),
  );

  return (
    <div className={styles.root}>
      {paymentMode === `finance` && priceDetails && (
        <div className={styles.row}>
          <Text variant="paragraph-200" tag="span">
            <strong>{leasingInformation?.labelForRepresentativeExample}</strong>
          </Text>
        </div>
      )}

      {(data?.attributes ?? [])
        .map(({label, key, endAdornment, labelSuperscript, helpertext}) => {
          if (!key) return label;
          const value = attributes.find((d) => d.key === key)?.value;
          if (!value) return;
          return (
            <div className={styles.row} key={key}>
              <Text variant="paragraph-200" tag="span">
                {label}
                {labelSuperscript && <sup>{labelSuperscript}</sup>}
              </Text>
              <div style={{textAlign: `right`}}>
                <Text variant="paragraph-200">{`${value}${
                  endAdornment ? ` ${endAdornment}` : ``
                }`}</Text>
                {helpertext ? (
                  <Text
                    className={styles.vatCaption}
                    variant="legal-100"
                    tag="span"
                  >
                    {helpertext}
                  </Text>
                ) : null}
              </div>
            </div>
          );
        })
        .filter((v) => v)}
    </div>
  );
}
